import { buttonStyle } from 'components/buttons/buttonStyle';
import ContactPickerModal from 'components/forms/form-components/select-picker/ContactPickerModal';
import AddContactModal from 'components/modals/contact/AddContactModal';
import { ContactTypePicker } from 'components/modals/contact/ContactTypePicker';
import { useModal } from 'components/modals/useModal';
import { FORM_OVERFLOW_LARGE_MODAL_STYLE } from 'components/modals/util';
import ContactDynamicSelectPicker from 'components/party/ContactDynamicSelectPicker';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { FormEvent, useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useLazyGetMinContactsListWithFiltersQuery } from 'store/api/contacts';
import { ContactMinDto, PartyType } from 'types/contacts';
import { t } from 'utils/getTranslations';
import { MinContact } from '../relatedPartyRows';

interface Props {
    roleName: string;
    allowsTypedName: boolean;
    onAddContact: (contact: MinContact) => void;
    onAddName: (name: string) => void;
    onCancel: () => void;
}

export function AddPartyRow({ roleName, allowsTypedName, onAddContact, onAddName, onCancel }: Props) {
    const { openModal, closeModal } = useModal();
    const [typedSearch, setTypedSearch] = useState('');
    const [isTypingName, setIsTypingName] = useState(false);
    const [typedName, setTypedName] = useState('');
    const { control } = useForm<{ contact: ContactMinDto | null }>({ defaultValues: { contact: null } });
    // The row appears on Add, and its typed-name form on "Type a name": typing goes straight into the field shown.
    const focusFirstInput = useCallback((row: HTMLElement | null) => {
        row?.querySelector('input')?.focus({ preventScroll: true });
    }, []);

    const createContact = (contactType: PartyType) =>
        openModal(
            <AddContactModal
                // The form closes itself after this.
                onContactAdd={onAddContact}
                onClose={closeModal}
                contactType={contactType}
                initialName={typedSearch}
            />,
            closeModal,
            FORM_OVERFLOW_LARGE_MODAL_STYLE
        );

    const chooseContactType = () =>
        openModal(
            <ContactTypePicker
                onCancel={closeModal}
                onSelect={(contactType) => {
                    closeModal();
                    createContact(contactType);
                }}
            />,
            closeModal,
            { width: '400px' }
        );

    const addTypedName = (event: FormEvent) => {
        event.preventDefault();
        if (typedName.trim()) {
            onAddName(typedName.trim());
        }
    };

    const cancelButton = (
        <Button {...buttonStyle({ variant: 'text', size: 'small' })} onClick={onCancel}>
            {t('remove')}
        </Button>
    );

    if (isTypingName) {
        return (
            <form
                ref={focusFirstInput}
                onSubmit={addTypedName}
                aria-label={t('client-identification.add-to-role', { role: roleName })}
                className="flex flex-wrap items-center gap-2"
            >
                <Input
                    value={typedName}
                    onChange={(event) => setTypedName(event.target.value)}
                    aria-label={t('client-identification.typed-name')}
                    className="min-w-0 flex-[1_1_16rem]"
                />
                <Button
                    type="submit"
                    {...buttonStyle({ variant: 'outlined', size: 'small' })}
                    disabled={!typedName.trim()}
                >
                    {t('client-identification.add-typed-name')}
                </Button>
                {cancelButton}
            </form>
        );
    }

    return (
        <div
            ref={focusFirstInput}
            role="group"
            aria-label={t('client-identification.add-to-role', { role: roleName })}
            className="flex flex-wrap items-start gap-2"
        >
            <div className="min-w-0 flex-[1_1_16rem]">
                <ContactDynamicSelectPicker
                    control={control}
                    // A contact picked or created in the picker joins the role; the field itself stays empty.
                    setValue={(_: 'contact', contact: MinContact) => onAddContact(contact)}
                    name="contact"
                    placeholder="client-identification.select-contact"
                    PickerModalComponent={ContactPickerModal}
                    paginated={{
                        fetch: useLazyGetMinContactsListWithFiltersQuery,
                        searchField: ['name', 'partyNumber'],
                    }}
                    onSearchChange={(eventType, searchValue) => {
                        if (eventType !== 'blur') {
                            setTypedSearch(searchValue);
                        }
                    }}
                    onChange={(contact) => {
                        if (contact) {
                            onAddContact(contact);
                        }
                    }}
                    wrapperClassName="gap-0"
                />
            </div>
            <Button {...buttonStyle({ variant: 'outlined', size: 'small' })} onClick={chooseContactType}>
                {t('client-identification.create-contact')}
            </Button>
            {allowsTypedName ? (
                <Button {...buttonStyle({ variant: 'text', size: 'small' })} onClick={() => setIsTypingName(true)}>
                    {t('client-identification.type-a-name')}
                </Button>
            ) : null}
            {cancelButton}
        </div>
    );
}
